import React, { useMemo, useState } from 'react'

import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    IconButton,
    CircularProgress,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
} from '@material-ui/core'
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Link as LinkIcon,
    FileCopy as FileCopyIcon,
    Check as CheckIcon,
    Visibility as VisibilityIcon,
    InfoOutlined as InfoIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { useGuestCards } from '../../../hooks/Outreach/useGuestCards'
import { useGuestCardChannelDefaults } from '../../../hooks/Outreach/useGuestCardChannelDefaults'
import { useLeadPipePixel } from '../../../hooks/Outreach/useLeadPipePixel'
import { CreateGuestCardModal } from '../Campaign/GuestCards/CreateGuestCardModal'
import { EditGuestCardModal } from '../Campaign/GuestCards/EditGuestCardModal'
import { ManageLinksModal } from './ManageLinksModal'
import { GuestCard } from '../../../models/Outreach/types'
import { GUEST_CARD_FIELD_QUESTIONS } from '../../../models/Outreach/services'
import { TabPanel } from '../../../components/TabPanel/TabPanel'

const useStyles = makeStyles((theme: Theme) => ({
    pageContainer: {
        padding: theme.spacing(4),
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        paddingBottom: theme.spacing(2.5),
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
    },
    headerContent: {
        flex: 1,
    },
    headerTitle: {
        fontSize: '2.25rem',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
        letterSpacing: '-0.02em',
    },
    headerSubtitle: {
        fontSize: '1rem',
        color: '#6b7280',
        fontWeight: 400,
    },
    tabsContainer: {
        backgroundColor: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.5)',
        borderRadius: theme.spacing(1),
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(0.5),
        minHeight: 'unset',
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minWidth: 140,
        minHeight: 40,
        borderRadius: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        padding: theme.spacing(1, 2),
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
        },
        '&.Mui-selected': {
            backgroundColor: '#3b82f6',
            color: 'white',
            fontWeight: 600,
        },
    },
    createButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1.5, 3),
        borderRadius: '10px',
        boxShadow:
            '0 4px 6px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -1px rgba(59, 130, 246, 0.2)',
        fontSize: '0.9375rem',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
            boxShadow:
                '0 10px 15px -3px rgba(59, 130, 246, 0.4), 0 4px 6px -2px rgba(59, 130, 246, 0.3)',
            transform: 'translateY(-1px)',
        },
        transition: 'all 0.2s ease',
    },
    searchField: {
        marginBottom: theme.spacing(3),
        maxWidth: 420,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#f9fafb',
            '&:hover': {
                backgroundColor: '#ffffff',
            },
            '&.Mui-focused': {
                backgroundColor: '#ffffff',
            },
        },
    },
    guestCardCard: {
        border: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
            boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-4px)',
            borderColor: '#3b82f6',
        },
    },
    cardContent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        gap: theme.spacing(1),
    },
    cardTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#111827',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
        minWidth: 0,
    },
    cardMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2.5),
        minHeight: 36,
    },
    defaultBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '6px',
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    linksBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '6px',
        backgroundColor: '#dcfce7',
        color: '#166534',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    linksBadgeEmpty: {
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
    },
    actionButton: {
        padding: theme.spacing(1),
        color: '#6b7280',
        borderRadius: '8px',
        flexShrink: 0,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f3f4f6',
            color: '#3b82f6',
        },
    },
    fieldsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    sectionHeader: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    sectionCount: {
        backgroundColor: '#e5e7eb',
        color: '#4b5563',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.6875rem',
        fontWeight: 600,
    },
    fieldList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    fieldItem: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: theme.spacing(1, 1.5),
        borderRadius: '8px',
        backgroundColor: '#f9fafb',
        fontSize: '0.875rem',
        color: '#374151',
        lineHeight: 1.5,
    },
    fieldCheck: {
        color: '#10b981',
        marginRight: theme.spacing(1),
        fontSize: '1rem',
        marginTop: '2px',
        flexShrink: 0,
    },
    fieldText: {
        flex: 1,
        wordBreak: 'break-word',
    },
    emptyFieldsText: {
        color: '#9ca3af',
        fontStyle: 'italic',
        fontSize: '0.875rem',
        padding: theme.spacing(1, 1.5),
    },
    manageLinksButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(0.5, 1.5),
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)',
        fontSize: '0.8125rem',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
            boxShadow: '0 4px 10px rgba(59, 130, 246, 0.35)',
        },
        transition: 'all 0.2s ease',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(10, 4),
    },
    emptyStateIcon: {
        fontSize: '5rem',
        color: '#d1d5db',
        marginBottom: theme.spacing(3),
        opacity: 0.5,
    },
    emptyStateTitle: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#111827',
        marginBottom: theme.spacing(1),
    },
    emptyStateText: {
        fontSize: '1rem',
        color: '#6b7280',
        maxWidth: '400px',
        margin: '0 auto',
        marginBottom: theme.spacing(3),
    },
    guestCardsSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2.5),
        gap: theme.spacing(2),
    },
    guestCardsSectionTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    guestCardsCount: {
        fontSize: '0.875rem',
        fontWeight: 400,
        color: '#9ca3af',
        marginLeft: theme.spacing(1),
    },
    pixelSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
    },
    pixelSectionIcon: {
        width: 36,
        height: 36,
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '1.125rem',
        boxShadow: '0 2px 8px rgba(109, 40, 217, 0.25)',
    },
    pixelSectionTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    pixelSectionSubtitle: {
        fontSize: '0.8125rem',
        color: '#9ca3af',
        fontWeight: 400,
    },
    pixelCard: {
        border: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        overflow: 'hidden',
    },
    pixelCardAccent: {
        borderLeft: '4px solid #8b5cf6',
    },
    pixelDetailsGrid: {
        display: 'flex',
        gap: theme.spacing(4),
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            gap: theme.spacing(2),
        },
    },
    pixelDetailItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    pixelLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    pixelValue: {
        fontSize: '0.9375rem',
        fontWeight: 500,
        color: '#111827',
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        width: 'fit-content',
    },
    statusBadgeActive: {
        backgroundColor: '#dcfce7',
        color: '#166534',
    },
    statusBadgePaused: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
    },
    statusDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
    },
    statusDotActive: {
        backgroundColor: '#22c55e',
        boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
    },
    statusDotPaused: {
        backgroundColor: '#f59e0b',
        boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.2)',
    },
    codeBlockWrapper: {
        position: 'relative',
        marginTop: theme.spacing(0.75),
    },
    codeBlock: {
        backgroundColor: '#0f172a',
        color: '#94a3b8',
        borderRadius: '12px',
        padding: theme.spacing(2.5, 2.5, 2.5, 2.5),
        paddingRight: theme.spacing(12),
        fontFamily:
            '"JetBrains Mono", "Fira Code", "Consolas", "Monaco", monospace',
        fontSize: '0.8125rem',
        lineHeight: 1.7,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        border: '1px solid #1e293b',
    },
    copyButtonOverlay: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '8px',
        padding: theme.spacing(0.75, 1.5),
        fontSize: '0.75rem',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#cbd5e1',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(4px)',
        minWidth: 'unset',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            color: '#f1f5f9',
        },
        transition: 'all 0.2s ease',
    },
    copyButtonSuccess: {
        backgroundColor: 'rgba(34, 197, 94, 0.15)',
        color: '#4ade80',
        borderColor: 'rgba(34, 197, 94, 0.3)',
        '&:hover': {
            backgroundColor: 'rgba(34, 197, 94, 0.2)',
            color: '#4ade80',
        },
    },
    pixelInstructions: {
        fontSize: '0.8125rem',
        color: '#9ca3af',
        marginTop: theme.spacing(2),
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #f1f5f9',
        lineHeight: 1.5,
    },
    pixelInstructionsIcon: {
        color: '#6366f1',
        fontSize: '1rem',
        marginTop: '1px',
        flexShrink: 0,
    },
    pixelCreateCard: {
        border: '2px dashed #d8b4fe',
        backgroundColor: '#faf5ff',
        borderRadius: '16px',
        textAlign: 'center',
    },
    pixelCreateContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(4, 3),
    },
    pixelCreateIcon: {
        width: 56,
        height: 56,
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
        fontSize: '1.5rem',
    },
    pixelCreateTitle: {
        fontSize: '1.0625rem',
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: theme.spacing(0.75),
    },
    pixelCreateDescription: {
        fontSize: '0.875rem',
        color: '#6b7280',
        marginBottom: theme.spacing(3),
        maxWidth: 420,
        lineHeight: 1.5,
    },
    pixelFormRow: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: 480,
    },
    pixelDomainInput: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#ffffff',
            borderColor: '#d8b4fe',
            '&:hover': {
                backgroundColor: '#ffffff',
            },
            '&.Mui-focused': {
                backgroundColor: '#ffffff',
            },
        },
    },
    pixelCreateButton: {
        background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1, 2.5),
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(109, 40, 217, 0.3)',
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
            boxShadow: '0 4px 12px rgba(109, 40, 217, 0.4)',
            transform: 'translateY(-1px)',
        },
        '&.Mui-disabled': {
            background: '#e5e7eb',
            color: '#9ca3af',
            boxShadow: 'none',
        },
        transition: 'all 0.2s ease',
    },
}))

export const WebToolsPage: React.FC = () => {
    const classes = useStyles()
    const { guestCards, isLoadingGuestCards, errorGuestCards } = useGuestCards()
    const { defaults: guestCardDefaults } = useGuestCardChannelDefaults()
    const defaultWebsiteGuestCardId = guestCardDefaults.website
    const { pixel, isLoading: isLoadingPixel, createPixel } = useLeadPipePixel()
    const [selectedTab, setSelectedTab] = useState(0)
    const [searchQuery, setSearchQuery] = useState('')
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [linksModalOpen, setLinksModalOpen] = useState(false)
    const [
        selectedGuestCard,
        setSelectedGuestCard,
    ] = useState<GuestCard | null>(null)
    const [pixelDomain, setPixelDomain] = useState('')
    const [copied, setCopied] = useState(false)

    const handleEdit = (guestCard: GuestCard) => {
        setSelectedGuestCard(guestCard)
        setEditModalOpen(true)
    }

    const handleManageLinks = (guestCard: GuestCard) => {
        setSelectedGuestCard(guestCard)
        setLinksModalOpen(true)
    }

    const handleCreateSuccess = () => {
        setCreateModalOpen(false)
    }

    const handleEditSuccess = () => {
        setEditModalOpen(false)
        setSelectedGuestCard(null)
    }

    const handleCreatePixel = () => {
        if (!pixelDomain.trim()) return
        createPixel.mutate({ domain: pixelDomain.trim() })
    }

    const handleCopyCode = async () => {
        if (!pixel?.code) return
        try {
            await navigator.clipboard.writeText(pixel.code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea')
            textarea.value = pixel.code
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const filteredGuestCards = useMemo(() => {
        const filtered =
            guestCards?.filter((card) =>
                card.card_name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
            ) || []
        if (defaultWebsiteGuestCardId == null) return filtered
        return [...filtered].sort((a, b) => {
            if (a.id === defaultWebsiteGuestCardId) return -1
            if (b.id === defaultWebsiteGuestCardId) return 1
            return 0
        })
    }, [guestCards, searchQuery, defaultWebsiteGuestCardId])

    const getEnabledFields = (
        card: GuestCard,
    ): Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> => {
        const fields: Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> = []
        if (card.name) fields.push('name')
        if (card.email) fields.push('email')
        if (card.phone) fields.push('phone')
        if (card.preferred_unit_config) fields.push('preferred_unit_config')
        if (card.preferred_premiums) fields.push('preferred_premiums')
        if (card.preferred_rent_cost) fields.push('preferred_rent_cost')
        if (card.preferred_move_in_date) fields.push('preferred_move_in_date')
        if (card.preferred_move_out_date) fields.push('preferred_move_out_date')
        if (card.preferred_roommates) fields.push('preferred_roommates')
        if (card.school_attendance) fields.push('school_attendance')
        if (card.additional_requests) fields.push('additional_requests')
        return fields
    }

    const guestCardCount = guestCards?.length || 0
    const showSearch = guestCardCount > 3 || searchQuery.length > 0

    if (isLoadingGuestCards) {
        return (
            <Box
                className={classes.pageContainer}
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    if (errorGuestCards) {
        return (
            <Box className={classes.pageContainer}>
                <Typography variant="h6" color="error">
                    Failed to load guest cards
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.pageContainer}>
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Typography className={classes.headerTitle}>
                        Web Tools
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        Manage tracking pixels, guest card forms, and shareable
                        links for your website
                    </Typography>
                </Box>
            </Box>

            <Tabs
                value={selectedTab}
                onChange={(_, newValue) => setSelectedTab(newValue)}
                className={classes.tabsContainer}
                TabIndicatorProps={{ style: { display: 'none' } }}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab
                    label={
                        guestCardCount > 0
                            ? `Guest Cards (${guestCardCount})`
                            : 'Guest Cards'
                    }
                    className={classes.tab}
                />
                <Tab label="Visitor Tracking" className={classes.tab} />
            </Tabs>

            <TabPanel
                value={selectedTab}
                index={0}
                style={{ width: '100%', flexDirection: 'column' }}
            >
                <Box className={classes.guestCardsSectionHeader}>
                    <Typography className={classes.guestCardsSectionTitle}>
                        Guest Cards
                        {guestCardCount > 0 && (
                            <span className={classes.guestCardsCount}>
                                {guestCardCount}{' '}
                                {guestCardCount === 1 ? 'form' : 'forms'}
                            </span>
                        )}
                    </Typography>
                    <Button
                        className={classes.createButton}
                        startIcon={<AddIcon />}
                        onClick={() => setCreateModalOpen(true)}
                        size="small"
                    >
                        Create Guest Card
                    </Button>
                </Box>

                {showSearch && (
                    <TextField
                        className={classes.searchField}
                        placeholder="Search guest cards..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: '#6b7280' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {filteredGuestCards.length > 0 ? (
                    <Grid container spacing={3}>
                        {filteredGuestCards.map((card) => {
                            const enabledFields = getEnabledFields(card)
                            const linkCount = card.links?.length || 0
                            return (
                                <Grid item xs={12} md={6} key={card.id}>
                                    <Card className={classes.guestCardCard}>
                                        <CardContent
                                            className={classes.cardContent}
                                        >
                                            <Box className={classes.cardHeader}>
                                                <Typography
                                                    className={
                                                        classes.cardTitle
                                                    }
                                                    title={card.card_name}
                                                >
                                                    {card.card_name}
                                                </Typography>
                                                <IconButton
                                                    className={
                                                        classes.actionButton
                                                    }
                                                    size="small"
                                                    onClick={() =>
                                                        handleEdit(card)
                                                    }
                                                    title="Edit guest card"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>

                                            <Box className={classes.cardMeta}>
                                                {card.id ===
                                                    defaultWebsiteGuestCardId && (
                                                    <Box
                                                        className={
                                                            classes.defaultBadge
                                                        }
                                                    >
                                                        Website default
                                                    </Box>
                                                )}
                                                {card.default &&
                                                    card.id !==
                                                        defaultWebsiteGuestCardId && (
                                                        <Box
                                                            className={
                                                                classes.defaultBadge
                                                            }
                                                        >
                                                            Default
                                                        </Box>
                                                    )}
                                                <Box
                                                    className={`${
                                                        classes.linksBadge
                                                    } ${
                                                        linkCount === 0
                                                            ? classes.linksBadgeEmpty
                                                            : ''
                                                    }`}
                                                >
                                                    {linkCount} link
                                                    {linkCount !== 1 ? 's' : ''}
                                                </Box>
                                                <Button
                                                    className={
                                                        classes.manageLinksButton
                                                    }
                                                    startIcon={<LinkIcon />}
                                                    onClick={() =>
                                                        handleManageLinks(card)
                                                    }
                                                    size="small"
                                                >
                                                    Manage links
                                                </Button>
                                            </Box>

                                            <Box className={classes.fieldsList}>
                                                <Box>
                                                    <Box
                                                        className={
                                                            classes.sectionHeader
                                                        }
                                                    >
                                                        Default Questions
                                                        {enabledFields.length >
                                                            0 && (
                                                            <span
                                                                className={
                                                                    classes.sectionCount
                                                                }
                                                            >
                                                                {
                                                                    enabledFields.length
                                                                }
                                                            </span>
                                                        )}
                                                    </Box>
                                                    {enabledFields.length >
                                                    0 ? (
                                                        <Box
                                                            className={
                                                                classes.fieldList
                                                            }
                                                        >
                                                            {enabledFields.map(
                                                                (field) => (
                                                                    <Box
                                                                        key={
                                                                            field
                                                                        }
                                                                        className={
                                                                            classes.fieldItem
                                                                        }
                                                                    >
                                                                        <span
                                                                            className={
                                                                                classes.fieldCheck
                                                                            }
                                                                        >
                                                                            ✓
                                                                        </span>
                                                                        <span
                                                                            className={
                                                                                classes.fieldText
                                                                            }
                                                                        >
                                                                            {
                                                                                GUEST_CARD_FIELD_QUESTIONS[
                                                                                    field
                                                                                ]
                                                                            }
                                                                        </span>
                                                                    </Box>
                                                                ),
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            className={
                                                                classes.emptyFieldsText
                                                            }
                                                        >
                                                            No default questions
                                                            selected
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {card.card_questions &&
                                                    card.card_questions.length >
                                                        0 && (
                                                        <Box>
                                                            <Box
                                                                className={
                                                                    classes.sectionHeader
                                                                }
                                                            >
                                                                Custom Questions
                                                                <span
                                                                    className={
                                                                        classes.sectionCount
                                                                    }
                                                                >
                                                                    {
                                                                        card
                                                                            .card_questions
                                                                            .length
                                                                    }
                                                                </span>
                                                            </Box>
                                                            <Box
                                                                className={
                                                                    classes.fieldList
                                                                }
                                                            >
                                                                {card.card_questions.map(
                                                                    (
                                                                        cardQuestion,
                                                                        index,
                                                                    ) => (
                                                                        <Box
                                                                            key={
                                                                                cardQuestion.id ||
                                                                                index
                                                                            }
                                                                            className={
                                                                                classes.fieldItem
                                                                            }
                                                                        >
                                                                            <span
                                                                                className={
                                                                                    classes.fieldCheck
                                                                                }
                                                                            >
                                                                                ✓
                                                                            </span>
                                                                            <span
                                                                                className={
                                                                                    classes.fieldText
                                                                                }
                                                                            >
                                                                                {
                                                                                    cardQuestion
                                                                                        .custom_question
                                                                                        .question
                                                                                }
                                                                            </span>
                                                                        </Box>
                                                                    ),
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                ) : (
                    <Card
                        className={classes.guestCardCard}
                        style={{
                            border: '2px dashed #e5e7eb',
                            backgroundColor: '#fafafa',
                        }}
                    >
                        <CardContent className={classes.emptyState}>
                            <Typography className={classes.emptyStateIcon}>
                                🌐
                            </Typography>
                            <Typography className={classes.emptyStateTitle}>
                                {searchQuery
                                    ? 'No guest cards found'
                                    : 'No guest cards yet'}
                            </Typography>
                            <Typography className={classes.emptyStateText}>
                                {searchQuery
                                    ? "Try adjusting your search query to find what you're looking for"
                                    : 'Create your first guest card to generate shareable links for your website'}
                            </Typography>
                            {!searchQuery && (
                                <Button
                                    className={classes.createButton}
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateModalOpen(true)}
                                >
                                    Create Guest Card
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}
            </TabPanel>

            <TabPanel
                value={selectedTab}
                index={1}
                style={{ width: '100%', flexDirection: 'column' }}
            >
                <Box className={classes.pixelSectionHeader}>
                    <Box className={classes.pixelSectionIcon}>
                        <VisibilityIcon style={{ fontSize: '1.125rem' }} />
                    </Box>
                    <Box>
                        <Typography className={classes.pixelSectionTitle}>
                            LeadPipe Visitor Tracking
                        </Typography>
                        <Typography className={classes.pixelSectionSubtitle}>
                            Identify anonymous website visitors
                        </Typography>
                    </Box>
                </Box>

                {isLoadingPixel ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress
                            size={28}
                            style={{ color: '#8b5cf6' }}
                        />
                    </Box>
                ) : pixel ? (
                    <Card
                        className={`${classes.pixelCard} ${classes.pixelCardAccent}`}
                    >
                        <CardContent>
                            <Box className={classes.pixelDetailsGrid}>
                                <Box className={classes.pixelDetailItem}>
                                    <Typography className={classes.pixelLabel}>
                                        Domain
                                    </Typography>
                                    <Typography className={classes.pixelValue}>
                                        {pixel.domain}
                                    </Typography>
                                </Box>
                                <Box className={classes.pixelDetailItem}>
                                    <Typography className={classes.pixelLabel}>
                                        Status
                                    </Typography>
                                    <Box
                                        className={`${classes.statusBadge} ${
                                            pixel.status === 'ACTIVE'
                                                ? classes.statusBadgeActive
                                                : classes.statusBadgePaused
                                        }`}
                                    >
                                        <Box
                                            className={`${classes.statusDot} ${
                                                pixel.status === 'ACTIVE'
                                                    ? classes.statusDotActive
                                                    : classes.statusDotPaused
                                            }`}
                                        />
                                        {pixel.status}
                                    </Box>
                                </Box>
                            </Box>

                            <Typography className={classes.pixelLabel}>
                                Embed Code
                            </Typography>
                            <Box className={classes.codeBlockWrapper}>
                                <Box className={classes.codeBlock}>
                                    {pixel.code}
                                </Box>
                                <Button
                                    className={`${classes.copyButtonOverlay} ${
                                        copied ? classes.copyButtonSuccess : ''
                                    }`}
                                    startIcon={
                                        copied ? (
                                            <CheckIcon
                                                style={{
                                                    fontSize: '0.875rem',
                                                }}
                                            />
                                        ) : (
                                            <FileCopyIcon
                                                style={{
                                                    fontSize: '0.875rem',
                                                }}
                                            />
                                        )
                                    }
                                    onClick={handleCopyCode}
                                >
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </Box>

                            <Box className={classes.pixelInstructions}>
                                <InfoIcon
                                    className={classes.pixelInstructionsIcon}
                                />
                                <span>
                                    Paste this snippet into the &lt;head&gt; tag
                                    of your website to start identifying
                                    visitors.
                                </span>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Card className={classes.pixelCreateCard}>
                        <CardContent>
                            <Box className={classes.pixelCreateContent}>
                                <Box className={classes.pixelCreateIcon}>
                                    <VisibilityIcon
                                        style={{
                                            fontSize: '1.5rem',
                                            color: '#7c3aed',
                                        }}
                                    />
                                </Box>
                                <Typography
                                    className={classes.pixelCreateTitle}
                                >
                                    Set Up Visitor Tracking
                                </Typography>
                                <Typography
                                    className={classes.pixelCreateDescription}
                                >
                                    Create a LeadPipe tracking pixel to identify
                                    anonymous visitors on your apartment website
                                    and turn them into actionable leads.
                                </Typography>
                                <Box className={classes.pixelFormRow}>
                                    <TextField
                                        className={classes.pixelDomainInput}
                                        placeholder="www.yourapartment.com"
                                        label="Website Domain"
                                        value={pixelDomain}
                                        onChange={(e) =>
                                            setPixelDomain(e.target.value)
                                        }
                                        variant="outlined"
                                        size="small"
                                        disabled={createPixel.isLoading}
                                    />
                                    <Button
                                        className={classes.pixelCreateButton}
                                        onClick={handleCreatePixel}
                                        disabled={
                                            !pixelDomain.trim() ||
                                            createPixel.isLoading
                                        }
                                    >
                                        {createPixel.isLoading ? (
                                            <CircularProgress
                                                size={20}
                                                style={{
                                                    color: 'white',
                                                }}
                                            />
                                        ) : (
                                            'Create Pixel'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}
            </TabPanel>

            <CreateGuestCardModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onGuestCardCreated={handleCreateSuccess}
            />

            {selectedGuestCard && (
                <EditGuestCardModal
                    open={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false)
                        setSelectedGuestCard(null)
                    }}
                    guestCard={selectedGuestCard}
                    onGuestCardUpdated={handleEditSuccess}
                />
            )}

            {selectedGuestCard && (
                <ManageLinksModal
                    open={linksModalOpen}
                    onClose={() => {
                        setLinksModalOpen(false)
                        setSelectedGuestCard(null)
                    }}
                    guestCard={selectedGuestCard}
                />
            )}
        </Box>
    )
}
